// src/pages/NetworkSolutionsPage.jsx
import React from "react";
import { Link, Element } from "react-scroll";
import "./NetworkSolutionsPage.scss";

const SERVICE_TILES = [
  {
    id: "structured-cabling",
    title: "Structured Cabling",
    subtitle: "Reliable voice & data backbone",
    img: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "network-switches",
    title: "Network Switches",
    subtitle: "High-performance LAN switching",
    img: "https://images.unsplash.com/photo-1587202372775-98973d4a04ab?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "servers",
    title: "Servers",
    subtitle: "Application & data servers",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "wireless-ap",
    title: "Wireless Access Point",
    subtitle: "Secure campus Wi-Fi",
    img: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "surveillance-biometric",
    title: "Surveillance & Biometric",
    subtitle: "CCTV & access control",
    img: "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?auto=format&fit=crop&w=800&q=80",
  },
];

export default function NetworkSolutionsPage() {
  return (
    <main className="network-page">
      {/* TOP: clickable tiles */}
      <section className="services-hero">
        <div className="container">
          <header className="services-hero__head">
            <h1>Network & Security Solutions</h1>
            <p>
              Explore our end-to-end solutions for structured cabling, switching,
              servers, wireless and surveillance. Click a card to jump to details.
            </p>
          </header>

          <div className="services-hero__grid">
            {SERVICE_TILES.map((service) => (
              <Link
                key={service.id}
                to={service.id}
                smooth={true}
                duration={600}
                offset={-80}
                className="service-tile"
              >
                <div className="service-tile__image">
                  <img src={service.img} alt={service.title} loading="lazy" />
                </div>
                <div className="service-tile__body">
                  <h2>{service.title}</h2>
                  <p>{service.subtitle}</p>
                  <span className="service-tile__cta">View details ▾</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM: detailed sections (scroll targets) */}
      <section className="services-details">
        <div className="container">
          {/* Structured Cabling */}
          <Element
            name="structured-cabling"
            className="service-block"
            id="structured-cabling"
          >
            <div className="service-block__text">
              <h2>Structured Cabling</h2>
              <h3>Products Offered – Norden · Molex · AMP</h3>
              <p>
                A structured cabling system is a complete system of cabling and
                associated hardware, providing a comprehensive telecommunications
                infrastructure for voice and data.
              </p>
              <p>
                Each installation is unique, depending on building architecture,
                cable and connection products, the function of the network, and
                future expansion needs. Industry standards define best practices
                for reliability and performance.
              </p>
              <p>We help you with:</p>
              <ul>
                <li>Consultation & site survey</li>
                <li>Design & implementation</li>
                <li>Testing & commissioning</li>
                <li>Documentation</li>
                <li>Maintenance & support services</li>
              </ul>
            </div>
            <div className="service-block__image">
              <img
                src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=900&q=80"
                alt="Structured cabling in a data rack"
                loading="lazy"
              />
            </div>
          </Element>

          {/* Network Switches */}
          <Element
            name="network-switches"
            className="service-block service-block--reverse"
            id="network-switches"
          >
            <div className="service-block__text">
              <h2>Network Switches</h2>
              <h3>
                Products Offered – Cisco · Juniper · D-Link · Netgear · Dell ·
                TP-Link · HP · Zyxel
              </h3>
              <p>
                Network switches connect multiple devices within a LAN and manage
                the flow of data by forwarding packets only to the intended
                destination ports. This improves both security and performance.
              </p>
              <p>
                We design switching architectures for access, distribution, and
                core layers, with proper VLANs, QoS and redundancy to ensure high
                availability.
              </p>
            </div>
            <div className="service-block__image">
              <img
                src="https://images.unsplash.com/photo-1587202372775-98973d4a04ab?auto=format&fit=crop&w=900&q=80"
                alt="Network switches in a rack"
                loading="lazy"
              />
            </div>
          </Element>

          {/* Servers */}
          <Element
            name="servers"
            className="service-block"
            id="servers"
          >
            <div className="service-block__text">
              <h2>Servers</h2>
              <h3>Products Offered – Cisco · HP · Dell · IBM</h3>
              <p>
                Servers provide services such as applications, databases, file
                storage, mail, web hosting and more to multiple client devices on
                the network.
              </p>
              <p>
                We size and deploy server infrastructure for performance,
                scalability and reliability, including virtualization, backups and
                high availability configurations.
              </p>
            </div>
            <div className="service-block__image">
              <img
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
                alt="Data center servers"
                loading="lazy"
              />
            </div>
          </Element>

          {/* Wireless Access Point */}
          <Element
            name="wireless-ap"
            className="service-block service-block--reverse"
            id="wireless-ap"
          >
            <div className="service-block__text">
              <h2>Wireless Access Point</h2>
              <h3>Products Offered – Cisco · Ruckus · D-Link · Juniper · Netgear · Aerohive</h3>
              <p>
                Wireless access points provide secure Wi-Fi connectivity across
                offices, campuses and public areas, reducing the need for
                extensive cabling.
              </p>
              <p>
                We design Wi-Fi coverage, capacity planning, roaming and security
                policies to ensure a robust wireless experience for all users.
              </p>
            </div>
            <div className="service-block__image">
              <img
                src="https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=900&q=80"
                alt="Wireless access point in office"
                loading="lazy"
              />
            </div>
          </Element>

          {/* Surveillance & Biometric */}
          <Element
            name="surveillance-biometric"
            className="service-block"
            id="surveillance-biometric"
          >
            <div className="service-block__text">
              <h2>Surveillance & Biometric</h2>
              <h3>
                Products Offered – Hikvision · CP-Plus · Honeywell · eSSL · HID · Secureye
              </h3>
              <p>
                CCTV systems help monitor and protect people, assets and
                premises. Biometric solutions provide secure, convenient identity
                verification using fingerprints, facial recognition, iris or
                voice.
              </p>
              <p>
                We provide end-to-end design, installation and integration of
                cameras, NVRs, access control and biometric attendance systems.
              </p>
            </div>
            <div className="service-block__image">
              <img
                src="https://images.unsplash.com/photo-1580894732444-8ecded7900cd?auto=format&fit=crop&w=900&q=80"
                alt="Security camera for surveillance"
                loading="lazy"
              />
            </div>
          </Element>
        </div>
      </section>
    </main>
  );
}
